import { DEFAULT_BINDINGS, DEFAULT_SETTINGS, SAVE_KEY } from '../core/constants';
import type {
  GameSettings,
  InputAction,
  InputBindings,
  ResolutionId,
  SaveData,
  SceneryDensity,
  SpeedUnit,
} from '../core/types';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface BestTimeUpdate {
  saveData: SaveData;
  previousBestMs: number | null;
  bestTimeMs: number | null;
  isNewBest: boolean;
}

export type SavePatch = Partial<Omit<SaveData, 'settings'>> & {
  settings?: Partial<Omit<GameSettings, 'bindings'>> & {
    bindings?: Partial<InputBindings>;
  };
};

const INPUT_ACTIONS: InputAction[] = [
  'accelerate',
  'brake',
  'left',
  'right',
  'handbrake',
  'restart',
  'reset',
  'pause',
];

const RESOLUTIONS = new Set<ResolutionId>([
  '1280x720',
  '1600x900',
  '1920x1080',
  '2560x1440',
]);
const SCENERY_DENSITIES = new Set<SceneryDensity>(['low', 'medium', 'high']);
const SPEED_UNITS = new Set<SpeedUnit>(['mph', 'kmh']);
const UNSAFE_RECORD_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cloneBindings(bindings: InputBindings): InputBindings {
  return {
    accelerate: [...bindings.accelerate],
    brake: [...bindings.brake],
    left: [...bindings.left],
    right: [...bindings.right],
    handbrake: [...bindings.handbrake],
    restart: [...bindings.restart],
    reset: [...bindings.reset],
    pause: [...bindings.pause],
  };
}

function cloneSettings(settings: GameSettings): GameSettings {
  return {
    ...settings,
    bindings: cloneBindings(settings.bindings),
  };
}

export function createDefaultSaveData(): SaveData {
  return {
    version: 1,
    selectedCarId: 'apex',
    selectedTrackId: 'first-run',
    bestTimes: {},
    settings: cloneSettings({
      ...DEFAULT_SETTINGS,
      bindings: cloneBindings(DEFAULT_BINDINGS),
    }),
  };
}

function readString(value: unknown, fallback: string): string {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim();
  return normalized.length > 0 && normalized.length <= 120 ? normalized : fallback;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return fallback;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function readVolume(value: unknown, fallback: number): number {
  const parsed = readNumber(value);
  return parsed === null ? fallback : Math.min(1, Math.max(0, parsed));
}

function normalizeBindingList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) {
    return [...fallback];
  }
  if (value.length === 0) {
    // An explicitly empty list is a valid result when an exclusive rebind
    // moves a key away from its previous action. Missing bindings still use
    // the defaults above.
    return [];
  }

  const uniqueCodes = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'string') {
      continue;
    }

    const code = item.trim();
    if (code.length > 0 && code.length <= 64) {
      uniqueCodes.add(code);
    }
  }

  return uniqueCodes.size > 0 ? [...uniqueCodes] : [...fallback];
}

function normalizeBindings(value: unknown): InputBindings {
  const source = isRecord(value) ? value : {};
  const bindings = {} as InputBindings;

  for (const action of INPUT_ACTIONS) {
    bindings[action] = normalizeBindingList(source[action], DEFAULT_BINDINGS[action]);
  }

  return bindings;
}

function normalizeSettings(value: unknown, legacyRoot: Record<string, unknown>): GameSettings {
  const source = isRecord(value) ? value : {};
  const resolution = source.resolution ?? legacyRoot.resolution;
  const sceneryDensity = source.sceneryDensity ?? legacyRoot.sceneryDensity;
  const speedUnit = source.speedUnit ?? legacyRoot.speedUnit;
  const bindings = source.bindings ?? legacyRoot.bindings;

  return {
    resolution:
      typeof resolution === 'string' && RESOLUTIONS.has(resolution as ResolutionId)
        ? (resolution as ResolutionId)
        : DEFAULT_SETTINGS.resolution,
    fullscreen: readBoolean(
      source.fullscreen ?? legacyRoot.fullscreen,
      DEFAULT_SETTINGS.fullscreen,
    ),
    vsync: readBoolean(source.vsync ?? legacyRoot.vsync, DEFAULT_SETTINGS.vsync),
    sceneryDensity:
      typeof sceneryDensity === 'string' &&
      SCENERY_DENSITIES.has(sceneryDensity as SceneryDensity)
        ? (sceneryDensity as SceneryDensity)
        : DEFAULT_SETTINGS.sceneryDensity,
    masterVolume: readVolume(
      source.masterVolume ?? legacyRoot.masterVolume,
      DEFAULT_SETTINGS.masterVolume,
    ),
    effectsVolume: readVolume(
      source.effectsVolume ?? legacyRoot.effectsVolume,
      DEFAULT_SETTINGS.effectsVolume,
    ),
    engineVolume: readVolume(
      source.engineVolume ?? legacyRoot.engineVolume,
      DEFAULT_SETTINGS.engineVolume,
    ),
    speedUnit:
      typeof speedUnit === 'string' && SPEED_UNITS.has(speedUnit as SpeedUnit)
        ? (speedUnit as SpeedUnit)
        : DEFAULT_SETTINGS.speedUnit,
    bindings: normalizeBindings(bindings),
  };
}

function normalizeBestTimes(value: unknown): Record<string, number> {
  if (!isRecord(value)) {
    return {};
  }

  const bestTimes: Record<string, number> = {};
  for (const [rawTrackId, rawTime] of Object.entries(value)) {
    const trackId = rawTrackId.trim();
    const time = readNumber(rawTime);
    if (
      trackId.length === 0 ||
      trackId.length > 120 ||
      UNSAFE_RECORD_KEYS.has(trackId) ||
      time === null ||
      time <= 0
    ) {
      continue;
    }

    bestTimes[trackId] = Math.round(time);
  }

  return bestTimes;
}

/**
 * Converts current or legacy save-shaped data into a complete v1 save. Every
 * nested collection is recreated, so callers cannot mutate defaults or the
 * object held by another load call.
 */
export function normalizeSaveData(value: unknown): SaveData {
  const defaults = createDefaultSaveData();
  if (!isRecord(value)) {
    return defaults;
  }

  // The aliases make unversioned development saves migrate without requiring
  // a brittle one-off migration pass.
  const selectedCar = value.selectedCarId ?? value.selectedCar;
  const selectedTrack = value.selectedTrackId ?? value.selectedTrack;
  const bestTimes = value.bestTimes ?? value.records ?? value.trackBestTimes;

  return {
    version: 1,
    selectedCarId: readString(selectedCar, defaults.selectedCarId),
    selectedTrackId: readString(selectedTrack, defaults.selectedTrackId),
    bestTimes: normalizeBestTimes(bestTimes),
    settings: normalizeSettings(value.settings, value),
  };
}

function resolveBrowserStorage(): StorageLike | null {
  try {
    return typeof globalThis.localStorage === 'undefined' ? null : globalThis.localStorage;
  } catch {
    // Privacy modes and sandboxed frames can throw merely by reading the
    // localStorage property. The in-memory fallback still keeps the game safe.
    return null;
  }
}

export class SaveSystem {
  readonly key: string;

  private readonly storage: StorageLike | null;
  private memoryValue: string | null = null;

  constructor(storage: StorageLike | null = resolveBrowserStorage(), key = SAVE_KEY) {
    this.storage = storage;
    this.key = key;
  }

  load(): SaveData {
    let raw = this.memoryValue;

    if (this.storage) {
      try {
        const storedValue = this.storage.getItem(this.key);
        if (storedValue !== null || raw === null) {
          raw = storedValue;
        }
      } catch {
        // Keep using the most recent in-memory value if storage becomes
        // inaccessible during the session.
      }
    }

    if (raw === null) {
      return createDefaultSaveData();
    }

    try {
      return normalizeSaveData(JSON.parse(raw) as unknown);
    } catch {
      return createDefaultSaveData();
    }
  }

  save(value: unknown): SaveData {
    const normalized = normalizeSaveData(value);
    const serialized = JSON.stringify(normalized, null, 2);
    this.memoryValue = serialized;

    if (this.storage) {
      try {
        this.storage.setItem(this.key, serialized);
      } catch {
        // A full quota or blocked storage must never interrupt a race. The
        // normalized in-memory copy remains available for this session.
      }
    }

    return normalizeSaveData(normalized);
  }

  update(patch: SavePatch): SaveData {
    const current = this.load();
    const nextSettings = patch.settings
      ? {
          ...current.settings,
          ...patch.settings,
          bindings: patch.settings.bindings
            ? { ...current.settings.bindings, ...patch.settings.bindings }
            : current.settings.bindings,
        }
      : current.settings;

    return this.save({
      ...current,
      ...patch,
      settings: nextSettings,
      bestTimes: patch.bestTimes ?? current.bestTimes,
    });
  }

  setSelectedCar(selectedCarId: string): SaveData {
    return this.update({ selectedCarId });
  }

  setSelectedTrack(selectedTrackId: string): SaveData {
    return this.update({ selectedTrackId });
  }

  updateSettings(settings: SavePatch['settings']): SaveData {
    return this.update({ settings });
  }

  getBestTime(trackId: string): number | null {
    return this.load().bestTimes[trackId] ?? null;
  }

  recordBestTime(trackId: string, timeMs: number): BestTimeUpdate {
    const saveData = this.load();
    const normalizedTrackId = trackId.trim();
    const previousBestMs = saveData.bestTimes[normalizedTrackId] ?? null;
    const validTime = Number.isFinite(timeMs) && timeMs > 0 ? Math.round(timeMs) : null;
    const isNewBest =
      normalizedTrackId.length > 0 &&
      normalizedTrackId.length <= 120 &&
      !UNSAFE_RECORD_KEYS.has(normalizedTrackId) &&
      validTime !== null &&
      (previousBestMs === null || validTime < previousBestMs);

    if (!isNewBest || validTime === null) {
      return {
        saveData,
        previousBestMs,
        bestTimeMs: previousBestMs,
        isNewBest: false,
      };
    }

    saveData.bestTimes[normalizedTrackId] = validTime;
    const persisted = this.save(saveData);
    return {
      saveData: persisted,
      previousBestMs,
      bestTimeMs: validTime,
      isNewBest: true,
    };
  }

  updateBestTime(trackId: string, timeMs: number): BestTimeUpdate {
    return this.recordBestTime(trackId, timeMs);
  }

  clear(): SaveData {
    this.memoryValue = null;
    if (this.storage) {
      try {
        this.storage.removeItem(this.key);
      } catch {
        // Falling back to defaults is sufficient if removal is blocked.
      }
    }
    return createDefaultSaveData();
  }
}

export const saveSystem = new SaveSystem();
